import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { Image3D } from '~/domain/patient/image3d';
import { describeSuvUnavailability } from '~/domain/patient/petImage';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { sameElements } from '~/utils/array';
import { enqueueSnackbar } from '~/utils/notifications';
import { petImageOf } from '~/viewportGrid/adapters/engine';

type ResidentReader = Pick<
    DatasetEnvironmentRegistry,
    'environmentFor' | 'listEntries' | 'subscribeLifecycle'
>;

/** The last list read per registry, held while its membership is unchanged. */
const petSnapshots = new WeakMap<ResidentReader, readonly Image3D[]>();

/**
 * Every PET image a resident Environment draws, in registry order, each
 * once. The same list comes back while the membership is unchanged, which
 * is what lets a store subscription settle and an effect key on the list.
 */
function readResidentPetImages(registry: ResidentReader): readonly Image3D[] {
    const images: Image3D[] = [];
    for (const entry of registry.listEntries()) {
        const environment = registry.environmentFor(entry.seriesUid);
        const pet = environment ? petImageOf(environment) : undefined;
        if (pet && !images.includes(pet)) {
            images.push(pet);
        }
    }
    const previous = petSnapshots.get(registry);
    if (previous && sameElements(previous, images)) {
        return previous;
    }
    petSnapshots.set(registry, images);
    return images;
}

/**
 * Warns once per PET image a resident row shows whose values cannot be
 * read as SUVbw, so the activity or counts fallback is never silent. A
 * series drawn on two rows counts once, and an image warned about is not
 * warned about again, whatever row shows it next.
 */
export function usePetSuvAvailabilityWarning(): void {
    const registry = getDatasetEnvironmentRegistry();
    const subscribe = useCallback(
        (listener: () => void) => registry.subscribeLifecycle(listener),
        [registry]
    );
    const read = useCallback(() => readResidentPetImages(registry), [registry]);
    const petImages = useSyncExternalStore(subscribe, read);

    const warned = useRef(new WeakSet<Image3D>());
    useEffect(() => {
        for (const pet of petImages) {
            if (warned.current.has(pet) || pet.suvFactor !== null) {
                continue;
            }
            warned.current.add(pet);
            enqueueSnackbar(describeSuvUnavailability(pet.suvQuantification), {
                variant: 'warning',
                preventDuplicate: true,
            });
        }
    }, [petImages]);
}
